import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCalendar, FiMapPin, FiTrendingUp, FiShield, FiUsers, FiCode } = FiIcons;

const Experience = ({ viewMode }) => {
  const experiences = [
    {
      title: "IT Security Engineer",
      company: "CeriFi",
      period: "Jun 2023 - Present",
      location: "Remote",
      type: "Full-time",
      description: "Leading the stand-up of the org-wide Office of Audit and Compliance, focusing on regulatory alignment and security framework enhancement.",
      achievements: [
        "Spearheaded review, remediation and build out of M365 security compliance and controls",
        "Lead efforts in obtaining and maintaining SOC 2 compliance, reinforcing client trust",
        "Orchestrated annual simulations to test and improve incident response times",
        "Administer all audit and compliance requests across all company brands",
        "Collaborate with legal teams to ensure security measures adhere to current laws"
      ],
      technologies: ["M365 Security", "SOC 2", "Compliance", "NIST", "Audit Management", "Incident Response"]
    },
    {
      title: "Senior IT Systems Engineer",
      company: "CeriFi",
      period: "Oct 2021 - Present",
      location: "Remote",
      type: "Full-time",
      description: "Serving as the lead Cyber-Security Technologist and championing major cloud migrations and infrastructure security.",
      achievements: [
        "Lead data migration for small, mid-sized, and large scale acquisitions",
        "Championed cloud-to-cloud migration for M365 tenant",
        "Administered roll-out of M365 Multi-Factor Authentication (MFA) for the entire organization",
        "Implemented Conditional Access Policies and newly acquired SaaS security services",
        "Oversee information governance and retention policies for Teams, SharePoint, and OneDrive"
      ],
      technologies: ["M365", "SharePoint", "Teams", "Azure AD", "MFA", "DLP", "SaaS Security"]
    },
    {
      title: "Systems Administrator",
      company: "Verus Investments",
      period: "Nov 2015 - Aug 2021",
      location: "Seattle, WA",
      type: "Full-time",
      description: "Head of the Cybersecurity Committee and lead for all networking and O365 administration.",
      achievements: [
        "Managed a fully-clustered Hyper-V environment and local backups (3+2+1 strategy)",
        "Enacted O365 security including anti-phishing, anti-spam, and eDiscovery",
        "Accountable for implementation of all Firewalls, Switches, VPN, and Network Policy Servers",
        "Ensured O365 Compliance, including GDPR via DLP policies",
        "Led the migration of internal IP into O365 SharePoint/OneDrive environment"
      ],
      technologies: ["Hyper-V", "Firewalls", "Azure AD Connect", "InTune", "VPN", "GDPR", "Networking"]
    },
    {
      title: "Director of IT",
      company: "Four Seasons Hotel Seattle",
      period: "Dec 2014 - Nov 2015",
      location: "Seattle, WA",
      type: "Full-time",
      description: "Tasked with PCI-DSS compliance and overseeing major technology infrastructure upgrades.",
      achievements: [
        "Replaced entire PBX phone system, eliminating three non-compliant Windows servers",
        "Facilitated a $2 million upgrade and renovation of the onsite restaurant",
        "Instituted new backup solutions allowing barebones recoveries and offsite storage",
        "Upgraded bandwidth to generate additional revenue through group rates",
        "Managed day-to-day operations for network, servers, and user support"
      ],
      technologies: ["PCI-DSS", "PBX Systems", "Network Management", "Project Management", "Vendor Management"]
    },
    {
      title: "Manager of IT",
      company: "Murray Franklyn Companies (Hotel 1000)",
      period: "Jan 2010 - Dec 2014",
      location: "Seattle, WA",
      type: "Full-time",
      description: "Orchestrated IT functions for the first fully-integrated Cisco hotel in the world.",
      achievements: [
        "Migrated the majority of the server environment to a Microsoft Hyper-V environment",
        "Managed complex vendor relationships (Cisco, GuestTek, SMS, Agilysys)",
        "Maintained operations for hotel staff, spa, restaurant, and luxury condo management",
        "Administered high-speed internet and dial-tone services with $50K annual budget",
        "Managed interactive systems including digital signage and IP-based room features"
      ],
      technologies: ["Cisco Systems", "Hyper-V", "Symantec", "IP Networking", "Hospitality Tech"]
    },
    {
      title: "Sr. Network Administrator",
      company: "Milliman",
      period: "Nov 2005 - Nov 2010",
      location: "Seattle, WA",
      type: "Full-time",
      description: "Focused on high-level network administration and infrastructure stability for a global firm.",
      achievements: [
        "Managed enterprise-scale network architecture and server maintenance",
        "Ensured 24/7 uptime for critical financial services infrastructure",
        "Collaborated on large-scale storage and backup solutions"
      ],
      technologies: ["Network Administration", "Enterprise Storage", "Server Management"]
    },
    {
      title: "I/T Manager & Network Administrator",
      company: "Medisystems",
      period: "Mar 2000 - Oct 2005",
      location: "Seattle, WA",
      type: "Full-time",
      description: "Dual role managing IT strategy and hands-on network administration.",
      achievements: [
        "Established core IT infrastructure for growing business operations",
        "Led technology procurement and implementation projects",
        "Maintained secure network environments for medical systems data"
      ],
      technologies: ["Windows Server", "Networking", "IT Management"]
    },
    {
      title: "User Support Specialist",
      company: "United States Air Force",
      period: "May 1995 - May 1999",
      location: "Los Angeles, CA",
      type: "Military Service",
      description: "Providing critical technical support and maintaining system readiness in a high-stakes environment.",
      achievements: [
        "Delivered technical support for Air Force personnel and systems",
        "Maintained adherence to strict military security protocols",
        "Developed foundational skills in hardware, software, and network troubleshooting"
      ],
      technologies: ["Hardware Support", "Network Troubleshooting", "System Maintenance"]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="section-padding bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <motion.div
          className="container mx-auto text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="font-heading text-5xl md:text-6xl font-bold mb-6">
            Professional Experience
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {viewMode === 'professional' ? 
              "A comprehensive trajectory of IT leadership and security engineering across finance, hospitality, and defense sectors." : 
              "From the Air Force to the front lines of cybersecurity - a decades-long adventure in technology and security."
            }
          </p>
        </motion.div>
      </section>

      {/* Timeline */}
      <section className="section-padding bg-white">
        <div className="container mx-auto">
          <motion.div 
            className="relative"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {/* Timeline Line */}
            <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-teal-400 to-teal-600"></div>

            {experiences.map((exp, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className={`relative flex items-center mb-16 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
              >
                {/* Timeline Dot */}
                <div className="absolute left-4 md:left-1/2 transform -translate-x-1/2 w-4 h-4 bg-teal-600 rounded-full border-4 border-white shadow-lg z-10"></div>

                {/* Content Card */}
                <motion.div 
                  className={`w-full md:w-5/12 ml-12 md:ml-0 ${index % 2 === 0 ? 'md:mr-auto md:pr-8' : 'md:ml-auto md:pl-8'}`}
                  whileHover={{ scale: 1.02, y: -5 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="bg-white rounded-xl shadow-xl p-8 border border-gray-100">
                    <div className="mb-6">
                      <h3 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                        {exp.title}
                      </h3>
                      <div className="text-lg font-semibold text-teal-600 mb-3">
                        {exp.company}
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center">
                          <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2" />
                          {exp.period}
                        </div>
                        <div className="flex items-center">
                          <SafeIcon icon={FiMapPin} className="w-4 h-4 mr-2" />
                          {exp.location}
                        </div>
                        <span className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-xs font-medium">
                          {exp.type}
                        </span>
                      </div>
                    </div>

                    <p className="text-gray-700 mb-6 leading-relaxed">
                      {exp.description}
                    </p>

                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                        <SafeIcon icon={FiTrendingUp} className="w-4 h-4 mr-2 text-teal-600" />
                        Key Achievements
                      </h4>
                      <ul className="space-y-2">
                        {exp.achievements.map((achievement, idx) => (
                          <li key={idx} className="flex items-start">
                            <div className="w-2 h-2 bg-teal-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <span className="text-gray-700 text-sm">{achievement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                        <SafeIcon icon={FiCode} className="w-4 h-4 mr-2 text-teal-600" />
                        Expertise & Focus
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {exp.technologies.map((tech, idx) => (
                          <span 
                            key={idx}
                            className="px-3 py-1 bg-gray-100 text-gray-800 rounded-lg text-sm font-medium hover:bg-teal-100 hover:text-teal-800 transition-colors"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section-padding bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <motion.div 
          className="container mx-auto text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <SafeIcon icon={FiShield} className="w-16 h-16 mx-auto mb-6 text-yellow-400" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Leverage Decades of Expertise
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            From architecture to compliance, I help organizations navigate the complex landscape of modern digital security.
          </p>
          <motion.a
            href="/#/contact"
            className="inline-flex items-center px-8 py-4 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon icon={FiUsers} className="mr-2 w-5 h-5" />
            Let's Collaborate
          </motion.a>
        </motion.div>
      </section>
    </div>
  );
};

export default Experience;